import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { cx } from '@emotion/css'
import { Field, reduxForm } from 'redux-form'
import {
  trackEvent,
  CountryCodePhoneInput,
  countryPhonePrefixRequired,
  phoneMinLength,
  InputWithRedux,
  FormErrorNotification,
  Heading,
  SingleSelect,
  SingleOption,
  FormFieldset,
  requiredField,
} from '@offerzen/pinata-shame'

import aboutYou from '../../../assets/about-page.svg'

import { updateProgress as reduxUpdateProgress } from '../../redux/modules/utility'
import { updateCandidate as reduxUpdateCandidate } from '../../redux/modules/candidate'
import Card from '../../components/Card'
import { previousOnboardingUrl, nextOnboardingUrl } from '../../helpers/onboardingSteps'
import CardFooterNav from '../../components/CardFooterNav/CardFooterNav'

import styles from './styles'

const PhoneInput = InputWithRedux(CountryCodePhoneInput)

const validators = {
  phoneMinLength: phoneMinLength(),
  countryPhonePrefixRequired: countryPhonePrefixRequired(),
  required: requiredField('Required'),
}

const CellphoneNumber = ({ candidateExists, updateProgress, ...props }) => {
  useEffect(() => {
    updateProgress(65)
    if (candidateExists) trackEvent('onboarding-contact-number-accessed')
  }, [updateProgress, candidateExists])

  return (
    <Card title="Contact details and notice period" imageUrl={aboutYou} width={550}>
      <If condition={props.error}>
        <FormErrorNotification text={props.error} />
      </If>

      <form onSubmit={props.handleSubmit}>
        <FormFieldset legend="What are your contact details?" className={styles.formFieldset}>
          <p className={styles.intro}>We’ll reach out to get you started and advise you during your job search.</p>

          <Field
            name="phone"
            className={styles.input}
            label="Cellphone number"
            validate={[validators.countryPhonePrefixRequired, validators.required, validators.phoneMinLength]}
            component={PhoneInput}
            onChange={(_, value) => {
              if (!value) {
                trackEvent('Candidate: profile action', {
                  action: 'cleared phone number',
                  source: 'onboarding',
                })
              }
            }}
            id="personal-information-phone"
            theme="primary"
          />

          <Field
            name="whatsapp_number"
            className={styles.input}
            label="WhatsApp number (if different from cellphone number)"
            validate={[validators.countryPhonePrefixRequired, validators.phoneMinLength]}
            helpText="Talent advisors prefer to use WhatsApp to support and advise you during your job search"
            component={PhoneInput}
            id="personal-information-whatsapp"
            theme="primary"
          />
        </FormFieldset>

        <Heading className={styles.heading}>How long is your notice period?</Heading>

        <Field
          component={SingleSelect}
          id="personal-information-notice-period"
          dataTest="personal-information-notice-period"
          label="Notice period length"
          placeholder="- select notice period length -"
          name="notice_period"
          selectClassName={cx(styles.input, styles.fullWidth)}
          validate={validators.required}
          theme="primary">
          <SingleOption label="0 weeks" value="0 weeks" />
          <SingleOption label="2 weeks" value="2 weeks" />
          <SingleOption label="4 weeks" value="4 weeks" />
          <SingleOption label="8 weeks" value="8 weeks" />
          <SingleOption label="Longer than 8 weeks" value="longer_than_8_weeks" />
        </Field>
        <CardFooterNav
          navigateBack={() => props.history.push(previousOnboardingUrl(props.location.pathname))}
          loading={props.loading}
          className={styles.footer}
          theme="primary"
        />
      </form>
    </Card>
  )
}

const mapStateToProps = state => ({
  candidateExists: !!state.candidate,
  loading: state.candidate.loading || false,
  initialValues: {
    phone: state.candidate.phone,
    whatsapp_number: state.candidate.whatsapp_number,
    notice_period: state.candidate.notice_period,
  },
})

const mapDispatchToProps = dispatch => ({
  updateCandidate: (candidate, nextRoute) => {
    dispatch(reduxUpdateCandidate(candidate, nextRoute))
  },
  updateProgress: progress => dispatch(reduxUpdateProgress(progress)),
})

CellphoneNumber.propTypes = {
  candidateExists: PropTypes.bool,
  updateProgress: PropTypes.func,
  loading: PropTypes.bool,
  handleSubmit: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  error: PropTypes.any,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
  }),
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(
    reduxForm({
      form: 'cellphoneNumber',
      onSubmit: (values, _, props) =>
        new Promise((resolve, reject) => {
          const url = nextOnboardingUrl(props.location.pathname)
          props.updateCandidate(
            {
              ...values,
              onboarding_phase: 'your_experience',
            },
            url,
          )
        }),
    })(CellphoneNumber),
  ),
)
